import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, SimpleChanges, ViewChild, PLATFORM_ID, Inject } from '@angular/core';
import { CommonModule, isPlatformServer, isPlatformBrowser } from '@angular/common';

import { debounceTime, fromEvent, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';

import { LazyLoadImageModule } from 'ng-lazyload-image';

import { State } from '@/models/graph';
import { Period } from '@/models/pipes';

import { StateService } from '@/services/state.service';
import { DataService } from '@/services/data.service';

import { WeiToEthPipe } from '@/pipes/wei-to-eth.pipe';
import { FormatCashPipe } from '@/pipes/format-cash.pipe';
import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';

// Import Chart.js v3 and plugins statically
import { 
  Chart, 
  TimeScale, 
  LinearScale, 
  CategoryScale,
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  Tooltip
} from 'chart.js';
import 'chartjs-adapter-moment';
import zoomPlugin from 'chartjs-plugin-zoom';
import crosshairPlugin from 'chartjs-plugin-crosshair';

    // Register base plugins that are SSR-safe
Chart.register(
  TimeScale,
  LinearScale,
  CategoryScale,
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  Tooltip
);

console.log('[ChartComponent] Chart.js v3 base plugins registered successfully');

// Add proper types for zoom plugin
interface ZoomPluginOptions {
  pan: {
    enabled: boolean;
    mode: 'x' | 'y' | 'xy';
    modifierKey?: string;
    onPan?: (context: unknown) => void;
    onPanStart?: (context: unknown) => void;
    onPanComplete?: (context: unknown) => void;
  };
  limits: {
    x?: {
      min: number;
      max: number;
      minRange: number;
    };
  };
  zoom: {
    mode: 'x' | 'y' | 'xy';
    wheel: {
      enabled: boolean;
      speed?: number;
    };
    drag: {
      enabled: boolean;
    };
    pinch: {
      enabled: boolean;
    };
  };
}

// Add crosshair plugin type
declare module 'chart.js' {
  interface PluginOptionsByType<TType> {
    crosshair: {
      line: {
        color: string;
        width: number;
      };
      zoom: {
        enabled: boolean;
      };
    };
  }
}

@Component({
  selector: 'app-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    LazyLoadImageModule,
    
    WeiToEthPipe,
    FormatCashPipe,
    TokenIdParsePipe
  ],
  providers: [
    WeiToEthPipe,
    FormatCashPipe
  ],
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss']
})

export class ChartComponent implements AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('canvas') canvasEl!: ElementRef<HTMLCanvasElement>;
  @ViewChild('tooltip') tooltipEl!: ElementRef<HTMLElement>;

  @Input() chartData!: State[] | null | undefined;
  @Input() period!: Period;

  chart!: any;
  config!: any;

  closestDataPoint!: State | null | undefined;
  xMin!: number;
  xMax!: number;

  private destroy$ = new Subject<void>();
  private panSubject = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    public dataSvc: DataService,
    private stateSvc: StateService,
    
    private wei2Eth: WeiToEthPipe,
    private formatCash: FormatCashPipe,

    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    // Don't setup chart functionality on server
    if (isPlatformServer(this.platformId)) {
      return;
    }
    
    // Setup debounced pan updates
    this.panSubject.pipe(
      debounceTime(16), // ~60fps
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.canvasEl.nativeElement.style.cursor = 'grabbing';
    });
  }

  async ngAfterViewInit(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      // Plugins are loaded dynamically on client side
      await this.createChart();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('[ChartComponent] ngOnChanges called:', changes);
    if (changes.period && !changes.period?.firstChange) {
      console.log('[ChartComponent] Period changed:', this.period);
      this.updateChart(this.period.limit);
    }

    if (changes.chartData && !changes.chartData?.firstChange) {
      console.log('[ChartComponent] ChartData changed:', this.chartData?.length, 'items');
      this.updateChart(this.period.limit);
    }
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  parseDate = (input: string) => {
    const parts = input.split('-');
    const numParts = parts.map((part: string) => Number(part));
    return new Date(numParts[0], numParts[1]-1, numParts[2]);
  }

  private validateInputs(): boolean {
    if (!this.chartData || !this.chartData.length) {
      console.warn('Chart data is empty or undefined');
      return false;
    }
    if (!this.period) {
      console.warn('Period is not defined');
      return false;
    }
    return true;
  }

  private getPinkColor(): string {
    if (isPlatformBrowser(this.platformId)) {
      const pinkValue = getComputedStyle(document.documentElement).getPropertyValue('--pink').trim();
      return pinkValue ? `rgba(${pinkValue}, 1)` : 'rgba(255, 4, 180, 1)';
    }
    return 'rgba(255, 4, 180, 1)';
  }

  private loadClientPlugins(): void {
    if (isPlatformBrowser(this.platformId)) {
      Chart.register(zoomPlugin, crosshairPlugin);
    }
  }

  private setupZoomLimits(): void {
    if (!this.chartData?.length) return;
    
    const chartData = this.chartData;
    this.xMin = this.parseDate(chartData[chartData.length - 1].id).getTime();
    this.xMax = this.parseDate(chartData[0].id).getTime();

    if (this.chart && this.config.options?.plugins?.zoom) {
      const zoomOptions = this.config.options.plugins.zoom as ZoomPluginOptions;
      zoomOptions.limits = {
        x: {
          min: this.xMin,
          max: this.xMax,
          minRange: 1000 * 60 * 60 * 24 * 7, // 7 days
        }
      };
      this.chart.update(); // Update without animation
    }
  }

  async createChart(): Promise<void> {
    // Don't create chart on server
    if (isPlatformServer(this.platformId)) {
      console.log('[ChartComponent] Skipping chart creation on server');
      return;
    }
    
    console.log('[ChartComponent] Creating chart on client');
    
    // Load client-side plugins dynamically
    await this.loadClientPlugins();
    
    if (!this.validateInputs()) return;

    try {
      const chartData = this.chartData!;
      const canvas = this.canvasEl.nativeElement;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        console.error('Failed to get canvas context');
        return;
      }

      canvas.height = 300;

      let volumeData = chartData.map(d => ({
        x: this.parseDate(d.id).getTime(),
        y: this.wei2Eth.transform(d.volume || '0')
      }));

      let floorData = chartData.map(d => ({
        x: this.parseDate(d.id).getTime(),
        y: this.wei2Eth.transform(d.floor || '0')
      }));

      const font: any = {
        size: 12,
        family: 'Montserrat, sans-serif',
        weight: 'bold',
      };

      this.xMin = this.parseDate(chartData[chartData.length - 1].id).getTime();
      this.xMax = this.parseDate(chartData[0].id).getTime();

      this.config = {
        type: 'bar',
        data: {
          datasets: [{
            label: 'Floor',
            data: floorData,
            type: 'line',
            fill: false,
            borderColor: environment.collection === 'cryptopunks-v1' ? 'rgba(166, 153, 254, 1)' : this.getPinkColor(),
            yAxisID: 'y2',
            tension: 0.1,
            pointStyle: false,
            pointRadius: 0,
            pointHoverRadius: 0
          }, {
            label: 'Volume',
            data: volumeData,
            backgroundColor: 'rgba(99, 133, 150, 1)',
            borderWidth: 0,
            barThickness: 'flex',
            yAxisID: 'y1',
          }]
        },
        options: {
          responsive: true,
          normalized: true,
          devicePixelRatio: 2,
          onResize: (chart: any, size: any) => {
            // console.log('onResize', chart, size)
            // this.canvasEl.nativeElement.style.cursor = 'default';
            return;
          },
          interaction: {
            mode: 'index',
            intersect: false,
          },
          scales: {
            x: {
              type: 'time',
              min: this.xMin,
              max: this.xMax,
              ticks: {
                font,
              },
              grid: {
                display: false,
              },
            },
            y1: {
              type: 'linear',
              position: 'right',
              ticks: {
                display: !this.stateSvc.getIsMobile(),
                callback: (value: number | string, i: number) => {
                  return this.zone.run(() => {
                    return 'Ξ' + this.formatCash.transform(value as number, 0);
                  });
                },
                font,
              },
              border: {
                display: false,
              },
              grid: {
                display: false,
              },
            },
            y2: {
              type: 'linear',
              position: 'left',
              ticks: {
                display: !this.stateSvc.getIsMobile(),
                callback: (value: number | string, i: number) => {
                  return this.zone.run(() => {
                    return 'Ξ' + this.formatCash.transform(value as number, 0);
                  });
                },
                font,
              },
              border: {
                display: false,
              },
              grid: {
                display: false,
              },
            }
          },
          plugins: {
            crosshair: {
              line: {
                color: 'rgba(255, 255, 255, 0.2)',
                width: 1,
              },
              zoom: {
                enabled: false,
              },
            },
            zoom: {
              pan: {
                enabled: true,
                mode: 'x',
                pinch: {
                  enabled: true 
                } as any,                
                onPan: () => {
                  this.panSubject.next();
                  return undefined;
                },
                onPanStart: () => {
                  this.canvasEl.nativeElement.style.cursor = 'grabbing';
                  return undefined;
                },
                onPanComplete: () => {
                  this.canvasEl.nativeElement.style.cursor = 'default';
                  this.setupZoomLimits(); // Ensure limits are correct after pan
                  return undefined;
                },
              },
              limits: {
                x: {
                  min: this.xMin,
                  max: this.xMax,
                  minRange: 1000 * 60 * 60 * 24 * 7, // 7 days
                },
              },
              zoom: {
                mode: 'x',
                wheel: {
                  enabled: false,
                  speed: 0.05,
                },
                drag: {
                  enabled: false,
                },
                pinch: {
                  enabled: false,
                },
              }
            },
            tooltip: {
              intersect: false,
              enabled: false,
              position: 'nearest',
              external: (context: any) => {
                const tooltipEl = this.tooltipEl.nativeElement;
                const { chart, tooltip } = context;
                const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
                const { caretX: tooltipX, caretY: tooltipY } = tooltip;
                const { width: tooltipWidth, height: tooltipHeight } = tooltipEl.getBoundingClientRect();
  
                const date = tooltip.dataPoints[0]?.raw.x;
                this.closestDataPoint = this.chartData!.find(d => this.parseDate(d.id).getTime() === date);
                this.cdr.markForCheck();
  
                if (context.tooltip.opacity === 0) {
                  this.closestDataPoint = null;
                  this.cdr.markForCheck();
                  tooltipEl.style.opacity = '0';
                  return;
                }
                
                tooltipEl.style.opacity = '1';
                if (!this.stateSvc.getIsMobile()) {
                  tooltipEl.style.transform = `translate(${positionX + tooltipX - tooltipWidth / 2}px, ${positionY + tooltipY - tooltipHeight - 10}px)`;
                }
                tooltipEl.style.pointerEvents = 'none';
                tooltipEl.style.transition = 'all 0.05s ease-in-out';
              },
            }
          },
        }
      };

      if (environment.collection === 'cryptopunks-v1') {
        this.config.data.datasets.splice(0, 1)
        delete this.config.options!.scales!.y2
      }

      this.chart = new Chart(ctx!, this.config);
      this.setupZoomLimits();
      this.updateChart(this.period.limit);

      // Handle window resize
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(250),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.chart.resize();
          this.setupZoomLimits();
        });

    } catch (error) {
      console.error('Error creating chart:', error);
    }
  }

  updateChart(n: number): void {
    console.log('[ChartComponent] updateChart called with n:', n);
    if (!this.validateInputs() || !this.chart) {
      console.warn('[ChartComponent] updateChart: validation failed or chart not ready');
      return;
    }

    this.zone.run(() => {
      try {
        const chartData = this.chartData!;
        console.log('[ChartComponent] Processing chartData:', chartData.length, 'items');
        
        let volumeData = chartData.map(d => ({
          x: this.parseDate(d.id).getTime(),
          y: this.wei2Eth.transform(d.volume || '0')
        }));

        let floorData = chartData.map(d => ({
          x: this.parseDate(d.id).getTime(),
          y: this.wei2Eth.transform(d.floor || '0')
        }));
    
        if (!volumeData || !floorData) {
          console.warn('[ChartComponent] Failed to process chart data');
          return;
        }

        console.log('[ChartComponent] Volume data points:', volumeData.length);
        console.log('[ChartComponent] Floor data points:', floorData.length);

        // Update the chart data
        console.log('[ChartComponent] Updating chart datasets...');
        if (environment.collection !== 'cryptopunks-v1') {
          console.log('[ChartComponent] Setting floor data:', floorData.length, 'points');
          this.config.data.datasets[0].data = floorData;
        }
        const volumeDatasetIndex = environment.collection === 'cryptopunks-v1' ? 0 : 1;
        console.log('[ChartComponent] Setting volume data at index', volumeDatasetIndex, ':', volumeData.length, 'points');
        this.config.data.datasets[volumeDatasetIndex].data = volumeData;

        const lastIndex = chartData.length - 1;
        const targetIndex = Math.min(n, lastIndex);
        
        // Assuming data is sorted from newest to oldest (most recent first)
        // If we want to show the last n days, we take from index 0 to targetIndex
        let maxDate = this.parseDate(chartData[0].id).getTime(); // Most recent date
        let minDate = this.parseDate(chartData[targetIndex].id).getTime(); // Date n days ago
        
        console.log('[ChartComponent] Data range:', {
          totalItems: chartData.length,
          targetIndex,
          maxDate: new Date(maxDate),
          minDate: new Date(minDate),
          maxDateItem: chartData[0].id,
          minDateItem: chartData[targetIndex].id
        });
        
        console.log('[ChartComponent] Setting zoom range:', { minDate, maxDate, targetIndex });
        
        this.setupZoomLimits(); // Ensure zoom limits are updated with new data
        
        // Use a more reliable way to update the chart view
        this.chart.options.scales.x.min = minDate;
        this.chart.options.scales.x.max = maxDate;
        
        console.log('[ChartComponent] Calling chart.update()');
        this.chart.update('none'); // Update without animation for better performance
      } catch (error) {
        console.error('[ChartComponent] Error updating chart:', error);
      }
    });
  }

    protected readonly environment = environment;
}
